import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { investmentAPI, fundAPI } from '../services/api';
import { ArrowLeft, Save } from 'lucide-react';

const InvestmentForm = () => {
  const navigate = useNavigate();
  const [funds, setFunds] = useState([]);
  const [formData, setFormData] = useState({
    fund_id: '',
    portfolio_company_name: '',
    investment_currency: 'USD',
    sector: '',
    types_of_securities_offered: [],
  });
  const [securityType, setSecurityType] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchFunds();
  }, []);

  const fetchFunds = async () => {
    try {
      const response = await fundAPI.getAll();
      setFunds(response.data);
    } catch (err) {
      console.error('Failed to fetch funds:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await investmentAPI.create(formData);
      navigate(`/investments/${response.data.id}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create investment');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const addSecurityType = () => {
    if (securityType.trim()) {
      setFormData({
        ...formData,
        types_of_securities_offered: [
          ...formData.types_of_securities_offered,
          securityType.trim(),
        ],
      });
      setSecurityType('');
    }
  };

  const removeSecurityType = (index) => {
    setFormData({
      ...formData,
      types_of_securities_offered: formData.types_of_securities_offered.filter(
        (_, i) => i !== index
      ),
    });
  };

  return (
    <div>
      <button
        onClick={() => navigate('/investments')}
        className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft size={20} className="mr-2" />
        Back to Investments
      </button>

      <div className="bg-white rounded-xl shadow-md p-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Create New Investment</h1>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Portfolio Company Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="portfolio_company_name"
                required
                value={formData.portfolio_company_name}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                data-testid="company-name-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fund <span className="text-red-500">*</span>
              </label>
              <select
                name="fund_id"
                required
                value={formData.fund_id}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                data-testid="fund-select"
              >
                <option value="">Select Fund</option>
                {funds.map((fund) => (
                  <option key={fund.id} value={fund.id}>
                    {fund.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Investment Currency <span className="text-red-500">*</span>
              </label>
              <select
                name="investment_currency"
                required
                value={formData.investment_currency}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="USD">USD</option>
                <option value="INR">INR</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Sector</label>
              <input
                type="text"
                name="sector"
                value={formData.sector}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Technology, Healthcare"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Types of Securities Offered
              </label>
              <div className="flex space-x-2 mb-2">
                <input
                  type="text"
                  value={securityType}
                  onChange={(e) => setSecurityType(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSecurityType())}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Equity, Preferred Shares"
                />
                <button
                  type="button"
                  onClick={addSecurityType}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Add
                </button>
              </div>
              {formData.types_of_securities_offered.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.types_of_securities_offered.map((type, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                    >
                      {type}
                      <button
                        type="button"
                        onClick={() => removeSecurityType(index)}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-4 pt-6 border-t">
            <button
              type="button"
              onClick={() => navigate('/investments')}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400"
              data-testid="create-investment-button"
            >
              <Save size={18} className="mr-2" />
              {loading ? 'Creating...' : 'Create Investment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InvestmentForm;
